<script setup lang="ts">
import { h } from 'vue'
import { type DataTableColumn, NFlex, NIcon, NPopover, NTag, NTooltip } from 'naive-ui'
import { formatDate } from '@/composables/useFormatDate'
import { sub, isPast, isFuture } from 'date-fns'
import type { ShopDetail } from '@/infrastructure/types'
import NavLinkComponent from './NavLinkComponent.vue'
import { useFetch } from '@/plugins/api'
import { CheckmarkCircle24Filled, CheckmarkCircle24Regular, Warning24Regular } from '@vicons/fluent'
import { useIntersectionObserver } from '@vueuse/core'
import { DataGapStatus } from '@/infrastructure/enums'

type Query = {
  groupIds?: (string | number)[]
  includeContracts?: boolean
  includeClosedShops?: boolean
  isDataApproved?: boolean | null
  companyIds?: number[] | null
  centerIds?: number[] | null
  locationIds?: number[] | null
}

interface Props {
  groupId: number | string
  lazy?: boolean
  isIntegrated?: boolean
  includeContracts?: boolean
  includeClosedShops?: boolean
  isDataApproved?: boolean | null
  companyIds?: number[] | null
  centerIds?: number[] | null
  locationIds?: number[] | null
}

const props = withDefaults(defineProps<Props>(), {
  lazy: false,
  isIntegrated: false,
  includeContracts: false,
  includeClosedShops: false,
  isDataApproved: null
})

defineExpose({
  getData
})

const shops = ref<ShopDetail[]>([])
const loading = ref(false)
const target = ref(null)
const isVisible = ref(false)
const initialLoad = ref(false)
const query = computed<Query>(() => ({
  groupIds: [props.groupId],
  includeContracts: props.includeContracts,
  includeClosedShops: props.includeClosedShops,
  isDataApproved: props.isDataApproved,
  companyIds: props.companyIds,
  centerIds: props.centerIds,
  locationIds: props.locationIds,
}))

const { stop } = useIntersectionObserver(
  target,
  ([{ isIntersecting }]) => {
    isVisible.value = isIntersecting
  },
  {
    rootMargin: '1000px',
    threshold: 0.3
  }
)

async function getData() {
  loading.value = true

  try {
    const data = await useFetch('shops', {
      ...query.value,
      includeContracts: true
    })

    shops.value = data
    initialLoad.value = true
  } finally {
    loading.value = false
    stop()
  }
}

watch(isVisible, (value) => {
  if (value && !initialLoad.value) {
    getData()
  }
})

watch(query, () => {
  if (!props.lazy) {
    getData()
  }
})

const shopColumns: DataTableColumn<ShopDetail>[] = [
  { title: 'Name', key: 'name', width: '20%', render: (shop) => h(NavLinkComponent, { to: `/groups/${props.groupId}/shops/${shop.id}` }, () => shop.name) },
  { title: 'Location', key: 'locationName', width: '15%' },
  {
    title: 'Category',
    key: 'shopCategoryName',
    width: '15%'
  },
  {
    title: 'Contract',
    key: 'contract',
    width: 130,
    render: (row) => {
      if (!row.contracts?.length) return null

      const activeContract = row.contracts
        .map(item => item.contract)
        .filter(contract => contract.start && contract.end)
        .find(contract => isPast(new Date(contract.start)) && isFuture(new Date(contract.end)))

      if (!activeContract) return null

      return h(NPopover, null, {
        trigger: () => activeContract.number,
        header: () => activeContract.name,
        default: () => `Period: ${formatDate(activeContract.start)} - ${formatDate(activeContract.end)}`
      })
    }
  },
  {
    title: 'Sales from',
    key: 'minTransactionDateTime',
    width: 110,
    render: (row) => {
      return row.minTransactionDateTime ? formatDate(row.minTransactionDateTime as string) : null
    }
  },
  {
    title: 'Sales to',
    key: 'maxTransactionDateTime',
    width: 110,
    render: (row) => {
      return row.maxTransactionDateTime ? formatDate(row.maxTransactionDateTime as string) : null
    }
  },
  {
    title: 'Data',
    key: 'data',
    align: 'right',
    width: 310,
    render: (row) => h(NFlex, { justify: 'end', align: 'center' }, () => [
      !row.dataGaps.filter(x => !x.closedOn).length ? null : h(NavLinkComponent, {
        to: `/data-gaps/?groupId=${row.groupId}&shopId=${row.id}&statuses=${DataGapStatus.MissingData}&statuses=${DataGapStatus.MissingReason}`,
        raw: true,
      }, {
        default: () => h(NTag, {
          type: 'warning',
          size: 'small',
          class: 'cursor-pointer! hover:underline'
        }, {
          default: () => 'Data gaps',
          icon: () => h(NIcon, {
            component: Warning24Regular
          })
        })
      }),
      !row.dataApprovedOn ? null : h(NTag, {
        type: 'success',
        size: 'small',
        component: CheckmarkCircle24Filled
      }, {
        default: () => 'Approved',
        icon: () => h(NIcon, {
          component: CheckmarkCircle24Regular
        })
      }),
      row.isInIndividualConsolidationPhase ? h(NTooltip, null, {
        trigger: () => h(NTag, { type: 'info', size: 'small' }, { default: () => 'Consolidation' }),
        default: () => formatDate(row.individualConsolidationPeriodFrom) + ' - ' + formatDate(row.individualConsolidationPeriodTo)
      }) : null,
      row.dataIssues ? h(NTag, { type: 'error', size: 'small' }, { default: () => 'Data issue' }) : null,
      props.isIntegrated && row.maxTransactionDateTime ? h(NavLinkComponent, {
        to: {
          path: 'sales',
          query: {
            groupId: props.groupId,
            shopId: row.id as number,
            from: sub(new Date(row.maxTransactionDateTime as string), { weeks: 1 }).toISOString(),
            to: row.maxTransactionDateTime as string,
            type: 'detailed'
          }
        }
      }, () => 'Detailed') : null,
      props.isIntegrated && row.maxTransactionDateTime ? h(NavLinkComponent, {
        to: {
          path: 'sales',
          query: {
            groupId: props.groupId,
            shopId: row.id as number,
            from: sub(new Date(row.maxTransactionDateTime as string), { weeks: 1 }).toISOString(),
            to: row.maxTransactionDateTime as string,
            type: 'daily',
          }
        }
      }, () => 'Daily') : null
    ])
  }
]
</script>

<template>
  <n-data-table
    ref="target"
    :columns="shopColumns"
    :data="shops"
    :bordered="false"
    :loading="loading"
    :bottom-bordered="false"
    size="small"
  ></n-data-table>
</template>